import React, { ReactNode, memo, ComponentProps } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { ThemeSpacing } from '@surglogs-app/ui.theme'

import { FlexBox } from './FlexBox'
import { Icon } from './Icon'

interface ListItemStyleProps {
  clickable?: boolean
  borderOffset: ThemeSpacing
  withBorder?: boolean
  withHoverEffect?: boolean
}

const StyledContainer = styled(FlexBox)<ListItemStyleProps>`
  position: relative;

  &::after {
    position: absolute;
    bottom: 0;
    left: ${({ theme, borderOffset }) => theme.ui.spacings[borderOffset]};
    content: '';
    height: ${({ theme, withBorder }) =>
      withBorder ? theme.ui.spacings.xxxs : theme.ui.spacings['abs.0']};
    width: calc(
      100% - 2 * ${({ theme, borderOffset }) => theme.ui.spacings[borderOffset]}
    );
    background-color: ${({ theme }) => theme.ui.colors['navy.10']};
  }

  &:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.ui.spacings.sm};
    border-top-right-radius: ${({ theme }) => theme.ui.spacings.sm};
  }

  &:last-of-type {
    &::after {
      display: none;
    }

    border-bottom-left-radius: ${({ theme }) => theme.ui.spacings.sm};
    border-bottom-right-radius: ${({ theme }) => theme.ui.spacings.sm};
  }

  ${({ theme, clickable, withHoverEffect }) =>
    withHoverEffect &&
    clickable &&
    css`
      &:hover {
        background-color: ${theme.ui.colors.light};
      }
    `};
`

interface ListItemProps
  extends Omit<ComponentProps<typeof StyledContainer>, 'borderOffset'> {
  children: ReactNode
  iconProps?: ComponentProps<typeof Icon>
  clickable?: boolean
  borderOffset?: ThemeSpacing
}

export const ListItem = memo(
  ({
    children,
    iconProps,
    clickable = false,
    cursor = clickable ? 'pointer' : 'auto',
    bgc = 'white.100',
    py = 'md',
    px = 'md',
    onClick,
    role = 'listitem',
    ai = 'center',
    borderOffset = 'abs.0',
    withBorder = true,
    withHoverEffect = true,
    ...rest
  }: ListItemProps) => (
    <StyledContainer
      {...{
        py,
        px,
        bgc,
        cursor,
        onClick: clickable ? onClick : undefined,
        role,
        clickable,
        ai,
        borderOffset,
        withBorder,
        withHoverEffect,
        ...rest,
      }}>
      {iconProps ? <Icon {...{ mr: 'md', ...iconProps }} /> : null}
      {children}
    </StyledContainer>
  ),
)
